import { useState } from "react";
import { useSEO } from "@/hooks/use-seo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "wouter";
import { ArrowRight, BookOpen, CheckCircle, Shield, ChevronDown, ChevronUp, Lock, CreditCard, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo1 from "@assets/logos_extracted/1.png";
import logo7 from "@assets/logos_extracted/7.png";
import logo8 from "@assets/logos_extracted/8.png";
import logo10 from "@assets/logos_extracted/10.png";
import flopImg from "@assets/image_1773078437357.png";
import handRankingsImg from "@assets/image_1773078441856.png";

const pokerCasinos = [
  {
    id: "rabona",
    name: "Rabona",
    logo: logo7,
    color: "#6e2400",
    wager: "Požadavek na protočení 35×",
    bonus: "až 12 500 Kč",
    bonusLabel: "Bonus",
    reward: "200",
    rewardLabel: "Freespiny",
    url: "https://rbn.fynkelto.com/?mid=279656_1645828",
  },
  {
    id: "casinia",
    name: "Casinia",
    logo: logo8,
    color: "#005a3c",
    wager: "Požadavek na protočení 35×",
    bonus: "až 12 500 Kč",
    bonusLabel: "Bonus",
    reward: "200",
    rewardLabel: "Freespiny",
    url: "https://csn.fynkelto.com/?mid=279656_1645832",
  },
  {
    id: "onlyspins",
    name: "OnlySpins",
    logo: logo1,
    color: "#003da5",
    wager: "Požadavek na protočení 35×",
    bonus: "až 12 500 Kč",
    bonusLabel: "Bonus",
    reward: "200",
    rewardLabel: "Freespiny",
    url: "https://ons.lynmonkel.com/?mid=279656_1882080",
    reviewUrl: "/kasina/onlyspins",
  },
  {
    id: "goldenpanda",
    name: "Golden Panda",
    logo: logo10,
    color: "#7a4a00",
    wager: "Požadavek na protočení 30×",
    bonus: "až 125 000 Kč",
    bonusLabel: "Bonus",
    reward: "50",
    rewardLabel: "Freespiny",
    url: "https://record.discasinoaffiliates.com/_kTMXKwmZsM98YAuMxhPI-WNd7ZgqdRLk/1/?pg=1",
  },
];

const comparisonRows = [
  { factor: "Tempo", online: "Rychlé, automatické", live: "Pomalé, více interakcí" },
  { factor: "Atmosféra", online: "Digitální, klidná", live: "Sociální, napjatá" },
  { factor: "Sázky", online: "Od mikro po high-stakes", live: "Obvykle vyšší minima" },
  { factor: "Dostupnost", online: "24/7 z domova", live: "Pouze v kasinu" },
  { factor: "Psychologie", online: "Menší vliv řeči těla", live: "Klíčový faktor" },
  { factor: "Statistiky a analýza", online: "Software pro tracking", live: "Ruční pozorování" },
];

const faqs = [
  { q: "Jaká je nejlepší pokerová varianta pro začátečníky?", a: "Nejčastěji se doporučuje Texas Hold'em, protože má jednoduchá pravidla a největší komunitu hráčů." },
  { q: "Co je silnější – flush nebo full house?", a: "Full house poráží flush, protože jde o kombinaci tří stejných karet a páru." },
  { q: "Jak funguje rake v pokeru?", a: "Rake je malý poplatek, který si kasino bere z každého potu jako provizi." },
  { q: "Jaké jsou aktuálně nejlepší poker kasina pro Čechy?", a: "PartyPoker, SynotTip a GG Poker patří mezi nejspolehlivější možnosti s českým nebo evropským licencováním." },
  { q: "Musím danit výhry z pokeru?", a: "Ano, pokud přesáhnou stanovený limit podle aktuálního daňového zákona. Doporučujeme konzultovat s poradcem." },
  { q: "Jak poznám férové kasino?", a: "Kontrolujte licenci, certifikát RNG, transparentní podmínky a recenze hráčů." },
  { q: "Můžu hrát poker zdarma?", a: 'Ano, většina platforem nabízí \u201ePlay Money\u201c režim nebo freerolly bez rizika.' },
  { q: "Jaký je minimální věk pro hraní v ČR?", a: "18 let – hráči mladší 18 let nemají legální přístup k hazardním hrám." },
];

function CasinoCard({ casino }: { casino: typeof pokerCasinos[0] }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden relative" data-testid={`poker-casino-${casino.id}`}>
      <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: "#003da5" }} />
      <div className="pl-5 pr-5 pt-4 pb-0 sm:pl-6 sm:pr-6 sm:pt-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0">
          <div className="flex items-center gap-2.5 flex-1 min-w-0 sm:pr-5">
            <div
              className="w-20 h-20 rounded-2xl flex-shrink-0 overflow-hidden"
              style={{ background: "transparent", boxShadow: `0 4px 14px #003da555` }}
            >
              <img src={casino.logo} alt={casino.name} className="w-full h-full object-cover" />
            </div>
            <div className="min-w-0">
              <div className="font-black text-gray-900 text-[15px] leading-snug">{casino.name}</div>
              <div className="text-[11px] mt-0.5 font-medium text-[#003da5]">{casino.wager}</div>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2 sm:border-l sm:border-gray-100 sm:pl-5 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="flex flex-col items-center justify-center px-3 py-2.5 rounded-lg min-w-[80px] bg-[#f0f5ff] border border-[#003da5]/15">
                <span className="font-black text-[#003da5] text-base leading-none">{casino.bonus}</span>
                <span className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mt-0.5">{casino.bonusLabel}</span>
              </div>
              <div className="flex flex-col items-center justify-center px-3 py-2.5 rounded-lg min-w-[80px] bg-gray-50 border border-gray-100">
                <span className="font-black text-gray-700 text-base leading-none">{casino.reward}</span>
                <span className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mt-0.5">{casino.rewardLabel}</span>
              </div>
            </div>
            <div className="flex flex-col gap-1.5 sm:pl-2 flex-shrink-0 sm:min-w-[114px]">
              <a href={casino.url} target="_blank" rel="sponsored noopener noreferrer">
                <button
                  className="hraj-btn w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg font-bold text-sm text-white transition-colors"
                  style={{ background: "#003da5" }}
                  onMouseEnter={e => (e.currentTarget.style.background = "#002d7c")}
                  onMouseLeave={e => (e.currentTarget.style.background = "#003da5")}
                  data-testid={`button-hraj-poker-${casino.id}`}
                >
                  Hraj <ArrowRight className="hraj-arrow w-3.5 h-3.5" />
                </button>
              </a>
              <a href={(casino as any).reviewUrl ?? `#recenze-${casino.id}`} className="flex items-center justify-center gap-1 py-1.5 text-[#003da5] text-xs font-semibold hover:text-[#002d7c] transition-colors">
                <BookOpen className="w-3 h-3 flex-shrink-0" /> Přečíst Recenzi
              </a>
            </div>
          </div>
        </div>

        <div className="mt-3 border-t border-gray-100/80 py-2.5 flex items-center justify-between flex-wrap gap-x-4 gap-y-1">
          <span className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500">
            <Lock className="w-3 h-3 text-gray-400" /> 18+ | Hrajte zodpovědně
          </span>
          <span className="text-[11px] text-gray-400 font-bold">Podmínky se vztahují</span>
        </div>
      </div>
    </div>
  );
}

function FaqItem({ faq, index, open, onToggle }: { faq: { q: string; a: string }; index: number; open: boolean; onToggle: () => void }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-testid={`poker-faq-${index}`}>
      <button
        className="w-full flex items-center justify-between gap-4 p-5 text-left"
        onClick={onToggle}
      >
        <span className="font-semibold text-gray-900 text-sm leading-normal pr-2">{faq.q}</span>
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-50 flex items-center justify-center text-gray-400">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-48 opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="px-5 pb-5">
          <div className="w-full h-px bg-gray-100 mb-3" />
          <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
        </div>
      </div>
    </div>
  );
}

export default function PokerPage() {
  useSEO({ title: "Poker 2026 – Rozcestník: pravidla, kombinace, strategie | HrajBonus", description: "Rozcestník pro české hráče pokeru. Jak hrát poker, pokerové kombinace, strategie, nejlepší online poker kasina a srovnání live vs. online hry. 18+",
  url: "https://hrajbonus.com/poker", });

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Poppins, sans-serif" }}>
      <Header />

      {/* HERO */}
      <section className="pt-16" style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }}>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-14 sm:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-white/70 text-xs font-semibold mb-4" data-testid="badge-aktualizovano-poker">
              <Calendar className="w-3 h-3" /> Aktualizováno: 12. března 2026
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-4">
              Poker – pravidla, strategie a průvodce pro české hráče
            </h1>
            <p className="text-white/75 text-base leading-relaxed">
              Poker je jednou z nejpopulárnějších karetních her na světě a své pevné místo má i mezi českými hráči. Na této stránce najdete kompletní průvodce pokerem – od základních pravidel, přes strategie až po speciální pravidla a tipy. Ať už chcete začít hrát online poker, nebo se připravujete na živou hru v kasinu, tento rozcestník vám nabídne vše potřebné.
            </p>
          </div>
        </div>
        <div className="relative z-10">
          <svg viewBox="0 0 1440 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" style={{ display: "block", verticalAlign: "bottom" }}>
            <path d="M0 40L1440 40L1440 0C1200 35 720 40 0 0L0 40Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Poker cross-navigation */}
      <nav className="bg-white border-b border-[#eef1f7] sticky top-16 z-20" data-testid="poker-nav">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="flex items-center gap-1 overflow-x-auto py-3 -mx-1">
            {[
              { href: "/poker", label: "Poker přehled", active: true },
              { href: "/poker/kombinace", label: "Kombinace", active: false },
              { href: "/poker/strategie", label: "Strategie", active: false },
              { href: "/poker/jak-hrat", label: "Jak hrát", active: false },
            ].map(({ href, label, active }) => (
              <Link key={href} href={href}>
                <div
                  className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                    active
                      ? "bg-[#003da5] text-white"
                      : "text-gray-600 hover:bg-[#f3f6fb] hover:text-[#003da5]"
                  }`}
                  data-testid={`poker-nav-${href.replace(/\//g, "-")}`}
                >
                  {label}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* CASINO CARDS */}
      <section className="py-12 bg-white -mt-1">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <h2 className="text-2xl font-black text-gray-900 mb-6">Nejlepší místa pro hraní pokeru</h2>
          <div className="flex flex-col gap-4">
            {pokerCasinos.map(casino => (
              <CasinoCard key={casino.id} casino={casino} />
            ))}
          </div>
        </div>
      </section>

      {/* JAK HRÁT POKER */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl font-black text-gray-900 mb-4">Jak hrát poker</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Poker je hra dovedností i strategie, kde cílem je sestavit co nejsilnější kombinaci pěti karet nebo přimět soupeře k položení karet. Nejrozšířenější variantou je Texas Hold'em. Hráči dostanou dvě skryté karty a postupně se rozdává pět společných karet na stůl. Klíčové jsou sázková kola – pre-flop, flop, turn a river.
              </p>
              <a href="#rankings">
                <Button className="hraj-btn font-bold gap-2 text-white" style={{ background: "#003da5" }}>
                  Naučte se hrát <ArrowRight className="hraj-arrow w-4 h-4" />
                </Button>
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden border border-gray-100 bg-white p-6 flex items-center justify-center">
              <img src={flopImg} alt="Flop, Turn a River v pokeru – pořadí komunitních karet" className="max-w-full h-auto object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* JE ONLINE POKER ZMANIPULOVANÝ */}
      <section className="py-12 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="max-w-3xl">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Je online poker zmanipulovaný?</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              Ne, u regulovaných a licencovaných kasin nikoli. Férovost hry je zajištěna technologií RNG (Random Number Generator) – generátoru náhodných čísel, který garantuje, že karty jsou rozdávány náhodně.
            </p>
            <p className="text-gray-600 leading-relaxed mb-3">
              Tento systém je testován a certifikován nezávislými laboratořemi, jako jsou{" "}
              <a href="https://www.ecogra.org/" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline font-semibold" data-testid="link-ecogra-poker">eCOGRA</a>, iTech Labs nebo GLI (Global Labs International).
            </p>
            <p className="text-gray-600 leading-relaxed">
              Chcete-li mít jistotu férovosti, hledejte v patičce kasina certifikát „RNG Tested" nebo odkaz na oficiální audit od{" "}
              <a href="https://www.ecogra.org/" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline font-semibold" data-testid="link-ecogra-audit">eCOGRA.org</a>.
            </p>
          </div>
        </div>
      </section>

      {/* PROČ HRÁT ONLINE POKER */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <h2 className="text-2xl font-black text-gray-900 mb-6">Proč hrát online poker</h2>
          <p className="text-gray-600 leading-relaxed mb-5">Online poker přináší výhody, které klasické kasino nemůže nabídnout:</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {[
              { title: "Flexibilita", desc: "Hrajte kdykoli, z domova nebo z mobilu." },
              { title: "Nižší limity", desc: "Ideální pro začátečníky." },
              { title: "Široká nabídka variant", desc: "Texas Hold'em, Omaha, Stud aj." },
              { title: "Rychlejší průběh", desc: "Více hand za hodinu než u live pokeru." },
              { title: "Anonymita a komfort", desc: "Žádný dress code, žádný stres." },
              { title: "Bonusové promoakce", desc: "Freerolly a bonusy u správného provozovatele." },
            ].map(item => (
              <div key={item.title} className="p-5 rounded-xl border border-gray-100 bg-white">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#003da5] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-gray-900 text-sm">{item.title}</div>
                    <div className="text-gray-500 text-xs mt-1">{item.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            Online poker může být stejně soutěživý jako turnaje v kasinu – a pokud se zaregistrujete u správného provozovatele, čekají vás i zajímavé freerolly a bonusové promoakce.
          </p>
        </div>
      </section>

      {/* STRATEGIE */}
      <section className="py-12 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-2xl font-black text-gray-900 mb-4">Pokerové strategie pro začátečníky i pokročilé</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Úspěch v pokeru není jen o štěstí, ale především o strategii. Mezi základy patří správný výběr startovních kombinací, disciplína a chápání významu pozice u stolu. Pokročilejší hráči využívají bluffování či čtení soupeřů.
              </p>
              <div className="rounded-xl border border-[#003da5]/15 p-5 mb-5" style={{ background: "linear-gradient(135deg, #f0f5ff 0%, #e8f0fe 100%)" }}>
                <h3 className="font-black text-gray-900 text-base mb-3">Pokerové pravidlo 7-2 a 10% pravidlo</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  V pokerové komunitě se tradují různé nepsané zásady, které dělají hru ještě zajímavější.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-2">
                  <span className="font-bold text-[#003da5]">7-2 pravidlo:</span> nejhorší startovní kombinace v Texas Hold'em, často využívaná k zábavným blufům.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  <span className="font-bold text-[#003da5]">10% pravidlo:</span> doporučení bankroll managementu – nikdy neriskujte více než 10 % svého bankrollu v jedné hře nebo sezení.
                </p>
              </div>
              <Link href="/poker/strategie">
                <Button className="hraj-btn font-bold gap-2 text-white" style={{ background: "#003da5" }}>
                  Přejít na pokerové strategie <ArrowRight className="hraj-arrow w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden border border-gray-100">
              <img src={handRankingsImg} alt="Pořadí pokerových kombinací – od nejvyšší po nejnižší" className="w-full h-auto object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* ONLINE VS LIVE TABLE */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <h2 className="text-2xl font-black text-gray-900 mb-2">Online poker vs. živý poker</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            Rozdíl mezi online pokerem a živou hrou v kasinu je výrazný. Online poker umožňuje rychlejší hru, více stolů najednou a širokou nabídku turnajů. Živý poker v kasinu přináší atmosféru, psychologické souboje a osobní kontakt se soupeři.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-gray-100">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "linear-gradient(135deg, #001a4d 0%, #003da5 100%)" }}>
                  <th className="text-left px-5 py-4 text-white font-bold">Faktor</th>
                  <th className="text-left px-5 py-4 text-white font-bold">Online Poker</th>
                  <th className="text-left px-5 py-4 text-white font-bold">Live Poker</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/70"}>
                    <td className="px-5 py-3.5 font-semibold text-gray-800">{row.factor}</td>
                    <td className="px-5 py-3.5 text-gray-600">{row.online}</td>
                    <td className="px-5 py-3.5 text-gray-600">{row.live}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-gray-500 text-sm leading-relaxed mt-4">
            Hra však není jen o kvalitě kombinace karet, ale také o taktice a nervech z oceli. Online hráči musí spoléhat na matematiku a analytické myšlení, zatímco live hráči na „čtení" soupeřů. Obě formy mají své kouzlo – online pro efektivitu, live pro atmosféru.
          </p>
        </div>
      </section>

      {/* 3 KLÍČOVÉ RADY */}
      <section className="py-12 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <h2 className="text-2xl font-black text-gray-900 mb-2">3 klíčové rady před tím, než začnete hrát o skutečné peníze</h2>
          <p className="text-gray-600 leading-relaxed mb-8">Online poker není jen štěstí, úspěch stojí na znalosti pravidel, strategii a disciplíně.</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                num: "1",
                title: "Ovládněte pravidla, pozice a kombinace",
                text: "Naučte se postup sázek, kombinace a význam pozice. Big Blind a Small Blind zásadně ovlivňují tempo i velikost potu. Ujistěte se, že rozumíte hodnotám hand ještě před tím, než vstoupíte do turnaje o peníze.",
              },
              {
                num: "2",
                title: "Pochopte hlavní principy pokerové strategie",
                text: "Online poker je mentální hra. Správné načasování bluffu, fold v pravý okamžik či agresivní raise vám ušetří nebo přidá velké množství žetonů. Výpočet odds a čtení situace je základ, který dnes používají všichni seriózní hráči.",
              },
              {
                num: "3",
                title: "Vyberte si variantu pokeru, která vám sedí",
                text: "Texas Holdem, Omaha nebo Stud – každá varianta má své tempo a množství akce. Pokud jste začátečník, začněte u Texas Hold'em, který je stále skladem ve všech českých online kasinech.",
              },
            ].map(item => (
              <div key={item.num} className="rounded-xl border border-gray-100 p-6 bg-white">
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-black text-white text-lg mb-4 flex-shrink-0" style={{ background: "#003da5" }}>
                  {item.num}
                </div>
                <h3 className="font-black text-gray-900 text-base mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-xl p-5 border border-[#003da5]/15" style={{ background: "linear-gradient(135deg, #f0f5ff 0%, #e8f0fe 100%)" }}>
            <p className="text-[#003da5] font-bold text-sm mb-1">VĚDĚLI JSTE, ŽE?</p>
            <p className="text-gray-700 text-sm leading-relaxed">
              Texas Hold'em má přes 100 milionů aktivních hráčů po celém světě a je součástí vzdělávacích programů na některých univerzitách kvůli psychologickému a pravděpodobnostnímu výzkumu.
            </p>
          </div>
        </div>
      </section>

      {/* JAK ZLEPŠIT HRU */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <h2 className="text-2xl font-black text-gray-900 mb-4">Jak zlepšit svou pokerovou hru</h2>
              <p className="text-gray-600 leading-relaxed mb-5">Úspěch v pokeru není o štěstí, ale o trpělivosti a neustálém zlepšování.</p>
              <ul className="flex flex-col gap-3">
                {[
                  "Hrajte, když se na to cítíte. Unavený mozek = chyby.",
                  "Učte se koncept GTO (Game Theory Optimal). Pomáhá vám hrát nevyužitelným způsobem.",
                  "Hrajte méně hand, ale agresivněji. Lepší kvalita než kvantita.",
                  "Buďte trpěliví. Čekejte na moment výhody.",
                  "Analyzujte soupeře. I v online prostředí je možné vysledovat vzorce.",
                  'Trénujte zdarma. Většina kasin nabízí \u201ePlay Money Mode\u201c.',
                  "Sledujte streamery. Např. Lex Veldhuis nebo Spraggy učí strategii v praxi.",
                ].map(tip => (
                  <li key={tip} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="rounded-xl p-5 border border-[#003da5]/15 mb-6" style={{ background: "linear-gradient(135deg, #f0f5ff 0%, #e8f0fe 100%)" }}>
                <p className="text-[#003da5] font-bold text-sm mb-1">VĚDĚLI JSTE, ŽE?</p>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Nejlepší profesionální hráči stráví až 60 % času studiem hand, analýzou dat a sledováním replayů – nejen hraním.
                </p>
              </div>

              <h3 className="font-black text-gray-900 text-lg mb-4">Online poker na mobilu</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Moderní aplikace nabízejí funkce jako chat, auto-bet, rychlé volby check/fold a optimalizované rozhraní pro dotyková zařízení.
              </p>
              <p className="text-gray-600 text-sm font-semibold mb-2">Nejlepší mobilní platformy v ČR v roce 2026:</p>
              <ul className="flex flex-col gap-2">
                {[
                  "Rabona – mobilní verze s plnou pokerovou sekcí a rychlými výplatami.",
                  "Casinia – optimalizované mobilní rozhraní, snadná registrace a vklady.",
                  "OnlySpins – moderní mobilní platforma s přehledným herním lobby.",
                ].map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ONLINE POKER S KAMARÁDY */}
      <section className="py-12 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <h2 className="text-2xl font-black text-gray-900 mb-4">Online poker s kamarády – kde si zahrát soukromě</h2>
          <p className="text-gray-600 leading-relaxed mb-5">
            Pokud hledáte zábavu s přáteli, můžete si vytvořit soukromý pokerový stůl online. Některé herní platformy nabízejí možnost „Private Table", tedy stůl přístupný jen pro pozvané hráče.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: "PokerStars Home Games", desc: "Ideální pro skupiny, jednoduché zakládání klubů." },
              { name: "Partypoker Private Tables", desc: "Možnost turnajů jen pro přátele." },
              { name: "Pokerrrr 2 App", desc: "Populární mobilní aplikace s virtuálními žetony." },
              { name: "888Poker Play with Friends", desc: "Možnost uzavřeného stolního turnaje." },
              { name: "GG Poker Clubs", desc: "Moderní herní systém s klubovým přístupem." },
            ].map(item => (
              <div key={item.name} className="p-4 rounded-xl border border-gray-100 bg-gray-50">
                <div className="font-bold text-gray-900 text-sm mb-1">{item.name}</div>
                <div className="text-gray-500 text-xs">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHRNUTÍ */}
      <section className="py-12" style={{ background: "linear-gradient(135deg, #001a4d 0%, #003da5 100%)" }}>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <h2 className="text-2xl font-black text-white mb-6">Shrnutí – Zahrajte si online poker chytře a zodpovědně</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              "Online poker v České republice je legální a díky moderním technologiím přístupnější než kdy dřív.",
              "Základem je vybírat licencované kasino, naučit se pravidla, znát strategie a správně pracovat s bankrollem.",
              "Poker není jen hra – je to kombinace psychologie, pravděpodobnosti a disciplíny.",
              "Ať už hrajete na mobilu nebo u stolu s přáteli, vždy si pamatujte na zodpovědné hraní a hranice svého rozpočtu.",
            ].map((text, i) => (
              <div key={i} className="p-5 rounded-xl border border-white/15 bg-white/08" style={{ background: "rgba(255,255,255,0.08)" }}>
                <p className="text-white/85 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
          <p className="text-white/50 text-xs mt-6">Pouze pro hráče 18+. Hrajte zodpovědně.</p>
        </div>
      </section>

      {/* POKEROVÉ PRŮVODCE */}
      <section className="py-12 bg-gray-50" data-testid="section-poker-pruvodce">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <h2 className="text-2xl font-black text-gray-900 mb-2">Prozkoumejte naše pokerové průvodce</h2>
          <p className="text-gray-500 text-sm mb-6">Vybrané stránky s detailními informacemi pro každou úroveň hráče</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <a
              href="/poker/jak-hrat"
              className="group flex flex-col p-5 rounded-xl border border-[#003da5]/15 bg-white hover:border-[#003da5]/40 hover:bg-[#f0f5ff] transition-colors"
              data-testid="link-pruvodce-jak-hrat"
            >
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-5 h-5 text-[#003da5]" />
                <span className="font-bold text-gray-900 text-sm group-hover:text-[#003da5] transition-colors">Jak hrát poker</span>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed mb-3">Pravidla Texas Hold'em krok za krokem, průběh hry, nejčastější chyby začátečníků a rozdíl mezi cash game a turnajem.</p>
              <div className="flex flex-wrap gap-1 mt-auto">
                {["Pravidla", "Začátečníci", "Texas Hold'em"].map(tag => (
                  <span key={tag} className="text-[10px] bg-[#f0f5ff] text-[#003da5] font-semibold px-2 py-0.5 rounded-full border border-[#003da5]/15">{tag}</span>
                ))}
              </div>
            </a>
            <a
              href="/poker/kombinace"
              className="group flex flex-col p-5 rounded-xl border border-[#003da5]/15 bg-white hover:border-[#003da5]/40 hover:bg-[#f0f5ff] transition-colors"
              data-testid="link-pruvodce-kombinace"
            >
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-[#003da5]" />
                <span className="font-bold text-gray-900 text-sm group-hover:text-[#003da5] transition-colors">Pokerové kombinace</span>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed mb-3">Přehled všech 10 kombinací od Royal Flush po High Card, kickery, remízy a praktické rady jak číst board rychle.</p>
              <div className="flex flex-wrap gap-1 mt-auto">
                {["Kombinace", "Royal Flush", "Kicker"].map(tag => (
                  <span key={tag} className="text-[10px] bg-[#f0f5ff] text-[#003da5] font-semibold px-2 py-0.5 rounded-full border border-[#003da5]/15">{tag}</span>
                ))}
              </div>
            </a>
            <a
              href="/poker/strategie"
              className="group flex flex-col p-5 rounded-xl border border-[#003da5]/15 bg-white hover:border-[#003da5]/40 hover:bg-[#f0f5ff] transition-colors"
              data-testid="link-pruvodce-strategie"
            >
              <div className="flex items-center gap-2 mb-2">
                <ArrowRight className="w-5 h-5 text-[#003da5]" />
                <span className="font-bold text-gray-900 text-sm group-hover:text-[#003da5] transition-colors">Pokerové strategie</span>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed mb-3">TAG styl, bluffing, bankroll management, pot odds a GTO koncepty pro hráče, kteří chtějí hrát na vyšší úrovni.</p>
              <div className="flex flex-wrap gap-1 mt-auto">
                {["Strategie", "Bluff", "Bankroll"].map(tag => (
                  <span key={tag} className="text-[10px] bg-[#f0f5ff] text-[#003da5] font-semibold px-2 py-0.5 rounded-full border border-[#003da5]/15">{tag}</span>
                ))}
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-6 bg-[#f6f8fc]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <p className="text-sm text-gray-600 leading-relaxed">
            <span className="font-bold text-gray-900">Hrajte poker v ověřeném kasinu: </span>
            <a href="/kasina" className="text-[#003da5] hover:underline font-semibold" data-testid="poker-link-kasina">Přehled kasin</a>
            {" · "}
            <a href="/kasina/onlyspins" className="text-[#003da5] hover:underline" data-testid="poker-link-onlyspins">OnlySpins</a>
            {" · "}
            <a href="/kasina/rabona" className="text-[#003da5] hover:underline" data-testid="poker-link-rabona">Rabona</a>
            {" · "}
            <a href="/kasina/nomini" className="text-[#003da5] hover:underline" data-testid="poker-link-nomini">Nomini</a>
            {" · "}
            <a href="/kasina/instant-casino" className="text-[#003da5] hover:underline" data-testid="poker-link-instant">Instant Casino</a>
            {" · "}
            <a href="/bonusy" className="text-[#003da5] hover:underline" data-testid="poker-link-bonusy">Bonusy</a>
            {" · "}
            <a href="/porovnat" className="text-[#003da5] hover:underline" data-testid="poker-link-porovnat">Porovnat kasina</a>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <h2 className="text-2xl font-black text-gray-900 mb-6">FAQ Sekce</h2>
          <div className="flex flex-col gap-2">
            {faqs.map((faq, i) => (
              <FaqItem
                key={i}
                faq={faq}
                index={i}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 pb-4">
        <div className="flex items-center justify-center gap-2 text-xs text-gray-400 font-medium">
          <Shield className="w-3.5 h-3.5" />
          <span>18+ | Hrajte zodpovědně |{" "}
            <a href="https://www.gamcare.org.uk/" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="link-gamcare-poker">GamCare</a>{" "}|{" "}
            <a href="https://www.gambleaware.org/" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="link-gambleaware-poker">GambleAware</a>
          </span>
        </div>
      </div>

      <Footer />
    </div>
  );
}
